import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import * as userApi from '../../api/userApi';
import * as groupApi from '../../api/groupApi';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Identity (who exists at all) is owned by Portal — people show up here
// automatically once Portal creates them and grants Material Master access.
// This mirrors MaterialWatch's Workers panel: Name / Employee ID / Role
// (designation) / Permission Group, with a single permission group assigned
// per person, editable here.
export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [groupId, setGroupId] = useState('');
  const [saving, setSaving] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { user: me, isAdmin, hasPermission } = useAuth();
  const canManagePermissions = isAdmin || hasPermission('MANAGE_PERMISSIONS');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([userApi.getAll(), groupApi.getAll()]);
      setUsers(u.data || u); setGroups(g.data || g);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (u) => {
    setEditItem(u);
    // Every user always resolves to a real group — their own explicit
    // assignment, or the current Default group as a fallback. There's no
    // "not assigned" state to preserve, so the dropdown always starts on
    // whichever group is actually in effect.
    setGroupId(u.permissionGroup?.id ?? '');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await userApi.update(editItem.id, {
        // Admin access (bypassing the permission group system entirely) is
        // not configurable from this page — it's a Super Admin-only Portal
        // concern, not something Material Master's own Users screen exposes.
        ...(canManagePermissions && { groupIds: groupId ? [groupId] : [] }),
      });
      showSnackbar('User updated', 'success');
      setEditItem(null); load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const groupList = groups.data || groups;
  const userList = users.data || users;

  return (
    <Box>
      <PageHeader title="Users" />

      <Card>
        {loading ? <LoadingSpinner /> : (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Employee ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Permission Group</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userList.map((u, i) => (
                <TableRow key={u.id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell fontWeight={600}>
                    {u.username} {u.id === me?.id && <Chip label="You" size="small" sx={{ ml: 0.5 }} />}
                    {u.role === 'ADMIN' && <Chip label="Admin" size="small" color="warning" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell>{u.employeeId || '—'}</TableCell>
                  <TableCell>{u.designation || '—'}</TableCell>
                  <TableCell>
                    {/* Every user always resolves to a real group (their own
                        explicit assignment, or the current Default group) —
                        there's no "not assigned" state to show. A Portal
                        super_admin is shown as "Admin Access" instead — they
                        bypass the permission-group system entirely, they were
                        never really "in" whatever group this shows. */}
                    {u.permissionGroup.isSynthetic ? (
                      <Chip label="Admin Access" size="small" color="info" />
                    ) : (
                      <Chip
                        label={u.permissionGroup.isImplicitDefault ? `${u.permissionGroup.name} (Default)` : u.permissionGroup.name}
                        size="small"
                        variant={u.permissionGroup.isImplicitDefault ? 'filled' : 'outlined'}
                        color={u.permissionGroup.isImplicitDefault ? 'warning' : 'default'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={u.isActive ? 'Active' : 'Inactive'} size="small" color={u.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    {/* Admin Access bypasses the permission-group system
                        entirely — there's nothing here to edit. */}
                    {!u.permissionGroup.isSynthetic && (
                      <IconButton size="small" onClick={() => openEdit(u)}><EditIcon fontSize="small" /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!userList.length && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No one has been synced from Portal yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </Box>
        )}
      </Card>

      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit — {editItem?.username}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <FormControl fullWidth disabled={!canManagePermissions}>
            <InputLabel>Permission Group</InputLabel>
            <Select value={groupId} label="Permission Group" onChange={(e) => setGroupId(e.target.value)}>
              {groupList.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </Select>
            {!canManagePermissions && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Requires the Manage Permissions permission
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
